import { sectionsForType } from "../forms/mortgageSchema";
import { productLabel } from "../forms/productCatalog";

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

const fmtGBP = (n) => {
  if (n === "" || n === null || n === undefined) return "";
  const num = Number(n);
  if (!isFinite(num)) return String(n);
  return `£${num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>");

const isMoneyField = (f) =>
  f.type === "money" || (f.type === "number" && /£/.test(f.label || ""));

const renderAddressHistoryHTML = (rows) => {
  const r = Array.isArray(rows) ? rows.filter((row) => row && (row.address || row.postcode || row.from || row.to)) : [];
  if (!r.length) return "<em style='color:#8a8f85;'>—</em>";
  return `<ul style="margin:0;padding-left:18px;list-style:disc;">
    ${r
      .map(
        (row) => `<li style="margin-bottom:3px;">
          ${esc([row.address, row.postcode].filter(Boolean).join(", "))}
          ${(row.from || row.to)
            ? `<span style="color:#5c6a60;"> · ${esc(fmtDDMMYYYY(row.from) || "?")} – ${esc(fmtDDMMYYYY(row.to) || "present")}</span>`
            : ""}
        </li>`
      )
      .join("")}
  </ul>`;
};

const renderValue = (field, v) => {
  if (field.type === "address_history") return renderAddressHistoryHTML(v);
  if (v === undefined || v === null || v === "") return "&nbsp;";
  if (field.type === "date") return esc(fmtDDMMYYYY(v));
  if (isMoneyField(field)) return esc(fmtGBP(v));
  return esc(v);
};

const renderGrid = (fields, data) => `
  <table class="grid">
    ${fields
      .filter((f) => f.type !== "signature" && f.type !== "commitments")
      .map(
        (f) => `
      <tr>
        <th>${esc(f.label)}${f.required ? '<span class="req">*</span>' : ""}</th>
        <td>${renderValue(f, data?.[f.id])}</td>
      </tr>`
      )
      .join("")}
  </table>
`;

const renderApplicantGrid = (fields, data) => {
  const a1 = data?.applicant_1 || {};
  const a2 = data?.applicant_2 || {};
  return `
    <table class="grid applicants">
      <thead>
        <tr>
          <th class="label">Detail</th>
          <th>1st Applicant</th>
          <th>2nd Applicant</th>
        </tr>
      </thead>
      <tbody>
        ${fields
          .filter((f) => f.type !== "signature")
          .map(
            (f) => `
          <tr>
            <th class="label">${esc(f.label)}${f.required ? '<span class="req">*</span>' : ""}</th>
            <td>${renderValue(f, a1[f.id])}</td>
            <td>${renderValue(f, a2[f.id])}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>`;
};

// Single-applicant mode — write out one column using applicant_1 only.
const renderSingleApplicantGrid = (fields, data) => {
  const a1 = data?.applicant_1 || {};
  return `
    <table class="grid">
      ${fields
        .filter((f) => f.type !== "signature")
        .map(
          (f) => `
        <tr>
          <th>${esc(f.label)}${f.required ? '<span class="req">*</span>' : ""}</th>
          <td>${renderValue(f, a1[f.id])}</td>
        </tr>`
        )
        .join("")}
    </table>`;
};

const renderCommitments = (rows) => {
  const r = Array.isArray(rows) ? rows : [];
  if (!r.length) return "<p class='empty-note'>No commitments recorded.</p>";
  return `
    <table class="grid commitments">
      <thead>
        <tr>
          <th>Applicant</th><th>Type</th><th>Company</th>
          <th class="num">Monthly</th><th class="num">Outstanding</th>
          <th class="num">Months Left</th><th>End Date</th><th class="num">Rate %</th>
        </tr>
      </thead>
      <tbody>
        ${r
          .map(
            (row) => `
          <tr>
            <td>${esc(row.applicant)}</td>
            <td>${esc(row.type)}</td>
            <td>${esc(row.company)}</td>
            <td class="num">${esc(fmtGBP(row.monthly))}</td>
            <td class="num">${esc(fmtGBP(row.outstanding))}</td>
            <td class="num">${esc(row.months_left)}</td>
            <td>${esc(fmtDDMMYYYY(row.end_date))}</td>
            <td class="num">${esc(row.rate)}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>`;
};

export const buildFormHtml = (submission) => {
  const type = submission.type || "mortgage";
  const sections = sectionsForType(type);
  const data = submission.data || {};
  const sigs = submission.signatures || {};
  // Legacy titles for mortgage/remortgage kept exactly as before; new products
  // use the product label from the catalog so the signed PDF reads naturally.
  const title = (type === "mortgage" || type === "remortgage")
    ? `${type === "remortgage" ? "Remortgage" : "Mortgage"} Application & Fact Find`
    : `${productLabel(type)} Application`;
  const applicantName = submission.customer_name || "";
  const signedDate = fmtDDMMYYYY(submission.signed_at || submission.created_at);
  const status = submission.status === "signed" ? "Signed" : "In Progress";
  const logoSrc = `${window.location.origin}/logo.png`;
  // Short human-friendly case reference derived from the stable form id. This
  // is what appears on the printable cover sheet and matches what the back
  // office can quote on the phone.
  const caseRef = submission.id
    ? `APP-${String(submission.id).replace(/-/g, "").slice(0, 6).toUpperCase()}`
    : "";
  const productLine = (type === "mortgage" || type === "remortgage") ? title : `${productLabel(type)} Application`;
  // Second applicant on the cover sheet when this is a joint application.
  // The schema stores name parts under data.applicant.applicant_2 — any
  // filled-in part flips the application into "Joint" mode. The authoritative
  // flag is `submission.applicant_type` set by the backend at form creation.
  const a2 = ((submission.data && submission.data.applicant) || {}).applicant_2 || {};
  const jointApplicantName = [a2.title, a2.first_name, a2.middle_name, a2.surname]
    .map((v) => (v || "").toString().trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  const isJoint = submission.applicant_type === "joint" || Boolean(jointApplicantName);

  const applicantType = submission.applicant_type || (isJoint ? "joint" : "single");
  const useJointGrid = applicantType === "joint" || isJoint;

  // Numbered sections for the step strip & section headers — skip declaration
  // because it's rendered separately at the end with the signatures block.
  const detailSections = sections.filter((s) => !s.isDeclaration && !s.isReview);
  const totalSteps = detailSections.length;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(applicantName || title)} — ${esc(title)}</title>
<style>
  /* A4 with generous print margins so nothing is cut off. The @top-left /
     @bottom-right CSS Paged Media rules repeat the brand title and page
     number on every physical sheet in Chrome's print pipeline. */
  @page {
    size: A4 portrait;
    margin: 20mm 16mm 20mm 16mm;
    @top-left {
      content: "Elin's Consultancy — ${esc(title)}";
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 9pt;
      color: #0F3226;
      letter-spacing: 0.08em;
    }
    @bottom-right {
      content: "Page " counter(page) " of " counter(pages);
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 9pt;
      color: #4a5a4e;
    }
  }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Georgia, 'Times New Roman', serif;
    color: #1C2620;
    font-size: 11pt;
    line-height: 1.45;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Dedicated one-page cover sheet shown first — lender-ready title page.
     Occupies a full A4 and forces a page break before the application body.
     The @page @top-left/@bottom-right rules are suppressed on this page via
     @page cover-page so the title page reads as a clean brand lockup. */
  @page cover-page {
    margin: 0;
    @top-left { content: ""; }
    @bottom-right { content: ""; }
  }
  .title-page {
    page: cover-page;
    page-break-after: always;
    break-after: page;
    height: 297mm;
    width: 100%;
    padding: 36mm 24mm 28mm;
    position: relative;
    background: #FDFCF6;
    border-top: 6pt solid #0F3226;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .title-page .brand {
    display: flex;
    align-items: center;
    gap: 18px;
  }
  .title-page .brand img.logo-cover {
    height: 110pt;
    width: auto;
  }
  .title-page .brand .brand-name {
    font-size: 9.5pt;
    letter-spacing: 0.34em;
    text-transform: uppercase;
    color: #8a6a1f;
    font-weight: 700;
  }
  .title-page .brand .brand-tagline {
    font-size: 10pt;
    color: #4a5a4e;
    margin-top: 4px;
    font-style: italic;
  }
  .title-page .headline {
    margin-top: auto;
    margin-bottom: auto;
    padding: 16mm 0;
    border-top: 1px solid #d4c58b;
    border-bottom: 1px solid #d4c58b;
  }
  .title-page .headline .eyebrow {
    font-size: 10pt;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: #8a6a1f;
    font-weight: 700;
    margin: 0 0 8pt;
  }
  .title-page .headline h1 {
    font-size: 34pt;
    line-height: 1.08;
    color: #0F3226;
    margin: 0 0 14pt;
    font-weight: 400;
  }
  .title-page .headline .applicant {
    font-size: 16pt;
    color: #1C2620;
    margin: 0;
  }
  .title-page .headline .applicant em {
    color: #4a5a4e;
    font-style: normal;
    font-size: 10.5pt;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    display: block;
    margin-bottom: 4pt;
    font-weight: 700;
  }
  .title-page .ref-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18pt;
    padding-top: 14mm;
    border-top: 1px solid #e0d4a8;
  }
  .title-page .ref-grid .cell .label {
    font-size: 8.5pt;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: #8a6a1f;
    font-weight: 700;
    margin: 0 0 4pt;
  }
  .title-page .ref-grid .cell .value {
    font-size: 12pt;
    color: #0F3226;
    margin: 0;
    font-weight: 700;
    letter-spacing: 0.03em;
  }
  .title-page .foot {
    text-align: center;
    font-size: 8.5pt;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #8a6a1f;
    font-weight: 700;
    margin-top: 12mm;
  }

  /* Cover block repeated as a table thead so Chrome will reprint the brand
     block whenever the main table flows onto a new page. For the first page
     we show the big cover; for later pages the @page @top-left handles the
     repeat so we don't need the fixed header duplicated. */
  .cover {
    border-bottom: 2px solid #0F3226;
    padding-bottom: 14px;
    margin: 0 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    page-break-after: avoid;
  }
  .cover .brand-block { display: flex; align-items: center; gap: 14px; }
  .cover img.logo { height: 68pt; width: auto; }
  .cover .brand-text .caption {
    font-size: 8.5pt;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #8a6a1f;
    font-weight: 700;
    margin: 0;
  }
  .cover h1 {
    font-size: 20pt;
    margin: 4px 0 0;
    color: #0F3226;
    line-height: 1.1;
  }
  .cover .meta {
    text-align: right;
    font-size: 10pt;
    color: #4a5a4e;
    line-height: 1.6;
    min-width: 200px;
  }
  .cover .meta div { white-space: nowrap; }
  .cover .meta strong { color: #0F3226; font-weight: 700; letter-spacing: 0.04em; }

  /* Compact numbered step strip reprinted on page 1 so the reader sees the
     full table of contents at a glance before the detail starts. */
  .step-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    margin: 0 0 22px;
    padding: 10px 12px;
    background: #FBF8F0;
    border: 1px solid #d4c58b;
    page-break-after: avoid;
  }
  .step-strip .step {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 7px;
    border: 1px solid #d4c58b;
    background: #fff;
    font-size: 8.5pt;
    letter-spacing: 0.02em;
    color: #4a5a4e;
    line-height: 1.3;
  }
  .step-strip .step .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    background: #0F3226;
    color: #F5F0E6;
    border-radius: 50%;
    font-size: 7.5pt;
    font-weight: 700;
  }
  .step-strip .step .label {
    font-size: 8.5pt;
    color: #0F3226;
    font-weight: 600;
  }

  .section {
    margin-bottom: 20px;
    page-break-inside: avoid;
  }
  /* New corporate section header: left gold accent bar + small-caps
     "SECTION X OF Y" + large serif title. Matches the back-office style. */
  .section-head {
    border-left: 3px solid #D4B468;
    padding: 2px 0 2px 12px;
    margin: 0 0 10px;
    page-break-after: avoid;
  }
  .section-head .step-caption {
    display: block;
    font-size: 8pt;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: #A68840;
    font-weight: 700;
    margin: 0 0 3px;
  }
  .section-head h2 {
    font-size: 15pt;
    color: #0F3226;
    margin: 0;
    font-weight: 600;
    letter-spacing: -0.005em;
    line-height: 1.15;
  }

  /* Gold asterisk inside grid labels for required fields. */
  .grid th .req {
    display: inline-block;
    margin-left: 3px;
    color: #A68840;
    font-weight: 700;
  }
  .section-desc {
    font-size: 9.5pt;
    color: #6a7a6e;
    margin: 0 0 10px;
    font-style: italic;
  }

  table.grid {
    width: 100%;
    border-collapse: collapse;
    page-break-inside: auto;
  }
  table.grid tr { page-break-inside: avoid; page-break-after: auto; }
  table.grid thead { display: table-header-group; }
  table.grid th, table.grid td {
    border: 1px solid #d4c58b;
    padding: 7px 9px;
    text-align: left;
    vertical-align: top;
    font-size: 10pt;
    line-height: 1.4;
  }
  table.grid th {
    background: #faf4e3;
    color: #0F3226;
    font-weight: 600;
    width: 34%;
  }
  table.grid.applicants th.label { width: 24%; }
  table.grid.applicants td { width: 38%; }
  table.grid td.num, table.grid th.num { text-align: right; }
  table.grid.commitments th { width: auto; font-size: 8.5pt; letter-spacing: 0.08em; text-transform: uppercase; }
  table.grid.commitments td { font-size: 9.5pt; }

  .empty-note {
    font-size: 9.5pt;
    color: #6a7a6e;
    font-style: italic;
  }

  .declaration {
    background: #faf4e3;
    border: 1px solid #d4c58b;
    padding: 14px 16px;
    margin-top: 12px;
    page-break-inside: avoid;
  }
  .declaration p { margin: 0 0 12px; font-size: 10.5pt; }
  .sigs {
    display: flex;
    gap: 22px;
    margin-top: 16px;
    flex-wrap: wrap;
  }
  .sig {
    flex: 1;
    min-width: 240px;
    page-break-inside: avoid;
  }
  .sig-box {
    border: 1px solid #0F3226;
    height: 90px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
  }
  .sig-box img {
    max-height: 78px;
    max-width: 100%;
    object-fit: contain;
  }
  .sig small {
    display: block;
    font-size: 8.5pt;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #0F3226;
    margin-top: 6px;
    font-weight: 700;
  }
  .sig .signed-date {
    font-size: 9.5pt;
    color: #4a5a4e;
    margin-top: 3px;
  }
</style>
</head>
<body>
  <section class="title-page">
    <div class="brand">
      <img class="logo-cover" src="${logoSrc}" alt="Elin's Consultancy" />
      <div>
        <div class="brand-name">Elin's Consultancy</div>
        <div class="brand-tagline">Property investment &amp; finance advisory</div>
      </div>
    </div>

    <div class="headline">
      <p class="eyebrow">${esc(status)}${isJoint ? " · Joint" : ""} Application</p>
      <h1>${esc(productLine)}</h1>
      <p class="applicant"><em>${isJoint ? "First Applicant" : "Applicant"}</em>${esc(applicantName || "—")}</p>
      ${isJoint ? `<p class="applicant" style="margin-top:12pt;"><em>Second Applicant</em>${esc(jointApplicantName)}</p>` : ""}
    </div>

    <div class="ref-grid">
      <div class="cell">
        <p class="label">Case Reference</p>
        <p class="value">${esc(caseRef || "—")}</p>
      </div>
      <div class="cell">
        <p class="label">${submission.status === "signed" ? "Signed" : "Prepared"}</p>
        <p class="value">${esc(signedDate || "—")}</p>
      </div>
      <div class="cell">
        <p class="label">Prepared For</p>
        <p class="value">Mortgage / Finance Broker</p>
      </div>
    </div>

    <div class="foot">Confidential — Prepared by Elin's Consultancy</div>
  </section>

  <div class="cover">
    <div class="brand-block">
      <img class="logo" src="${logoSrc}" alt="Elin's Consultancy" />
      <div class="brand-text">
        <p class="caption">Elin's Consultancy</p>
        <h1>${esc(title)}</h1>
      </div>
    </div>
    <div class="meta">
      <div><strong>Applicant:</strong> ${esc(applicantName)}</div>
      <div><strong>Date:</strong> ${esc(signedDate)}</div>
      <div><strong>Status:</strong> ${esc(status)}</div>
    </div>
  </div>

  <div class="step-strip" aria-label="Sections in this document">
    ${detailSections
      .map(
        (s, i) => `
      <span class="step">
        <span class="num">${i + 1}</span>
        <span class="label">${esc(s.title)}</span>
      </span>`
      )
      .join("")}
    <span class="step"><span class="num">${totalSteps + 1}</span><span class="label">Client Declaration &amp; Signature</span></span>
  </div>

  ${detailSections
    .map((s, i) => {
      const body = s.applicants
        ? (useJointGrid
            ? renderApplicantGrid(s.fields, data[s.id])
            : renderSingleApplicantGrid(s.fields, data[s.id]))
        : s.fields.some((f) => f.type === "commitments")
          ? (() => {
              const nonCommit = renderGrid(
                s.fields.filter((f) => f.type !== "commitments"),
                data[s.id]
              );
              const sd = data[s.id] || {};
              if (useJointGrid) {
                return `${nonCommit}
             <h3 style="font-size:9.5pt;margin:14px 0 6px;color:#0F3226;letter-spacing:.14em;text-transform:uppercase;font-weight:700;">1st Applicant — Commitments</h3>
             ${renderCommitments(sd.applicant_1_commitments)}
             <h3 style="font-size:9.5pt;margin:14px 0 6px;color:#0F3226;letter-spacing:.14em;text-transform:uppercase;font-weight:700;">2nd Applicant — Commitments</h3>
             ${renderCommitments(sd.applicant_2_commitments)}`;
              }
              return `${nonCommit}
             <h3 style="font-size:9.5pt;margin:14px 0 6px;color:#0F3226;letter-spacing:.14em;text-transform:uppercase;font-weight:700;">Commitments</h3>
             ${renderCommitments(sd.commitments_table)}`;
            })()
          : renderGrid(s.fields, data[s.id]);
      return `<div class="section">
        <div class="section-head">
          <span class="step-caption">Section ${i + 1} of ${totalSteps}</span>
          <h2>${esc(s.title)}</h2>
        </div>${
          s.description ? `<p class="section-desc">${esc(s.description)}</p>` : ""
        }${body}</div>`;
    })
    .join("")}

  <div class="section">
    <div class="section-head">
      <span class="step-caption">Section ${totalSteps + 1} of ${totalSteps + 1}</span>
      <h2>Client Declaration &amp; Signature</h2>
    </div>
    <div class="declaration">
      <p>I/We confirm that the information provided in this form is, to the best of my/our knowledge, complete and accurate, and I/we authorise Elin's Consultancy to act on my/our behalf.</p>
      <div class="sigs">
        <div class="sig">
          <div class="sig-box">${
            sigs.signature_applicant_1
              ? `<img src="${sigs.signature_applicant_1}" alt="Applicant 1 signature" />`
              : ""
          }</div>
          <small>${useJointGrid ? "Applicant 1 Signature" : "Signature"}</small>
          <div class="signed-date">${
            sigs.signed_at ? "Signed on " + fmtDDMMYYYY(sigs.signed_at) : ""
          }</div>
        </div>
        ${useJointGrid ? `
        <div class="sig">
          <div class="sig-box">${
            sigs.signature_applicant_2
              ? `<img src="${sigs.signature_applicant_2}" alt="Applicant 2 signature" />`
              : ""
          }</div>
          <small>Applicant 2 Signature</small>
          <div class="signed-date">${
            sigs.signed_at && sigs.signature_applicant_2
              ? "Signed on " + fmtDDMMYYYY(sigs.signed_at)
              : ""
          }</div>
        </div>` : ""}
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const printFormPdf = (submission) => {
  const html = buildFormHtml(submission);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const d = iframe.contentWindow.document;
  d.open();
  d.write(html);
  d.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => iframe.remove(), 1500);
    }
  }, 400);
};
